import { Avatar, Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLayoutEffect, useRef, useState } from "react";
import { euro } from "../utils/Currency";

// Indicador flotante de que hay más botones desplazándose fuera de la vista
const arrowSx = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    color: 'text.secondary'
};

export default function OrderTypes({ orderType, handleOrderType, scheduleList }) {
    // Con 3 o más tipos los botones no caben: la fila hace scroll horizontal
    const scrollable = scheduleList.length > 2;
    const groupRef = useRef(null);
    const [position, setPosition] = useState({ atStart: true, atEnd: true });

    const updateArrows = () => {
        const group = groupRef.current;
        if (!group) return;
        const maxScroll = group.scrollWidth - group.clientWidth;
        setPosition({
            atStart: group.scrollLeft <= 1,
            atEnd: maxScroll <= 1 || group.scrollLeft >= maxScroll - 1
        });
    };

    useLayoutEffect(() => {
        const group = groupRef.current;
        if (!group) return;

        let cancelled = false;
        const refresh = () => {
            if (!cancelled) updateArrows();
        };

        refresh();
        const observer = new ResizeObserver(refresh);
        observer.observe(group);
        group.addEventListener('scroll', refresh, { passive: true });
        // Las tipografías pueden cambiar el ancho de los botones al cargar
        document.fonts?.ready?.then(refresh);

        return () => {
            cancelled = true;
            observer.disconnect();
            group.removeEventListener('scroll', refresh);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scheduleList.length, scrollable]);

    return (
        <Box sx={{ position: 'relative', marginTop: '10px', width: '100%' }}>
            <ToggleButtonGroup
                ref={groupRef}
                value={orderType}
                exclusive
                onChange={handleOrderType}
                color="secondary"
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'nowrap',
                    ...(scrollable && {
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' }
                    })
                }}
            >
                {scheduleList.length > 0 && scheduleList.map((type) => (
                    <ToggleButton
                        key={type.id}
                        value={type.id}
                        sx={
                            scrollable
                                // En móvil cada botón ocupa la mitad de la fila (se ven 2 y el resto se desplaza).
                                // En escritorio el carrito tiene ancho fijo y caben los 3: se reparten el espacio.
                                ? { flex: { xs: '0 0 auto', md: 1 }, width: { xs: '50%', md: 'auto' }, minWidth: 0 }
                                : { flex: 1 }
                        }
                    >
                        <Avatar
                            variant="rounded"
                            alt={type.name}
                            src={`data:image/png;base64,${type.image}`}
                            sx={{ width: 28, height: 28, marginInline: 1, bgcolor: 'inherit', color: 'inherit' }}
                        />
                        {type.name}
                        {type.charge > 0 && ` (+${euro.format(type.charge)})`}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            {scrollable && !position.atStart && (
                <Box
                    aria-hidden
                    sx={{
                        ...arrowSx,
                        left: 0,
                        background: 'linear-gradient(to right, rgba(255,255,255,0.95), rgba(255,255,255,0))'
                    }}
                >
                    <ChevronLeftIcon fontSize="small" />
                </Box>
            )}
            {scrollable && !position.atEnd && (
                <Box
                    aria-hidden
                    sx={{
                        ...arrowSx,
                        right: 0,
                        background: 'linear-gradient(to left, rgba(255,255,255,0.95), rgba(255,255,255,0))'
                    }}
                >
                    <ChevronRightIcon fontSize="small" />
                </Box>
            )}
        </Box>
    );
}
